import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  ReactNode,
} from 'react';
import { authService } from './auth';
import { queryClient } from '@/shared/queryClient';
import { notificationService } from './notifications';
import { subscribeSessionInvalidation } from './sessionInvalidation';

interface AuthContextType {
  isInitialized: boolean;
  isAuthenticated: boolean;
  user: any | null;
  isLoading: boolean;
  logout: () => void;
  setAuthenticated: (user: any) => void;
  resetAuthState: () => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function useAuthContext() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuthContext must be used within an AuthProvider');
  }
  return context;
}

interface AuthContextProviderProps {
  children: ReactNode;
}

export function AuthContextProvider({ children }: AuthContextProviderProps) {
  const [isInitialized, setIsInitialized] = useState(false);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [user, setUser] = useState<any | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  const logout = useCallback(async () => {
    console.log("Logging out from context");
    await authService.clearSession();
    setUser(null);
    setIsAuthenticated(false);
    queryClient.setQueryData(["auth", "user"], null);
    queryClient.removeQueries({ queryKey: ['auth'] }); // More surgical
    console.log("Logout completed, auth state cleared");
  }, []);

  useEffect(() => {
    return subscribeSessionInvalidation(() => {
      void logout();
    });
  }, [logout]);

  const setAuthenticated = (userData: any) => {
    console.log("Setting authenticated user in context:", userData);
    setUser(userData);
    setIsAuthenticated(true);
    // Also update query cache
    queryClient.setQueryData(["auth", "user"], userData);
    console.log("Authentication state updated in context");
  };

  const resetAuthState = () => {
    console.log("Resetting auth state in context");
    setUser(null);
    setIsAuthenticated(false);
    queryClient.setQueryData(["auth", "user"], null);
  };

  useEffect(() => {
    const initializeAuth = async () => {
      try {
        console.log("Initializing auth context...");
        setIsLoading(true);
        
        // Initialize the auth service
        await authService.initialize();
        
        // Check if we have a restored session
        const restoredUser = await authService.getRestoredUser();
        if (restoredUser) {
          setUser(restoredUser);
          setIsAuthenticated(true);
          
          // Set in query cache for other components
          queryClient.setQueryData(["auth", "user"], restoredUser);

          // Backfill device registration if not already registered for this employee (fire-and-forget)
          const employeeId = restoredUser?.id;
          if (typeof employeeId === 'number') {
            setTimeout(() => {
              notificationService.ensureDeviceRegisteredIfNeeded(employeeId).catch(() => {});
            }, 0);
          }
        } else {
          console.log("No restored session found");
          setIsAuthenticated(false);
          setUser(null);
        }
        
        setIsInitialized(true);
        console.log("Auth context initialized");
      } catch (error) {
        console.error("Failed to initialize auth context:", error);
        setIsAuthenticated(false);
        setUser(null);
        setIsInitialized(true);
      } finally {
        setIsLoading(false);
      }
    };

    initializeAuth();
  }, []);

  // Remove the query cache subscription to prevent infinite loops
  // We'll rely on direct method calls to update the auth context

  const value: AuthContextType = {
    isInitialized,
    isAuthenticated,
    user,
    isLoading,
    logout,
    setAuthenticated,
    resetAuthState,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}
